import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import DialogContentText from '@mui/material/DialogContentText';
import { useTranslation } from 'react-i18next';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.min.css';

import { Button } from 'common/components/Button';
import { AlertDialog } from 'common/components/AlertDialog';
import { useSnackbar } from 'common/hooks/useSnackbar';

interface IProps {
    open: boolean;
    imageFile: File | null;
    aspectRatio?: number;
    minWidth?: number;
    minHeight?: number;
    onConfirm(croppedFile: File): void;
    onCancel(): void;
}

export const ImageCropperDialog = ({
    open,
    imageFile,
    aspectRatio,
    minWidth,
    minHeight,
    onConfirm,
    onCancel,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const imageRef = useRef<HTMLImageElement>(null);
    const cropperRef = useRef<Cropper | null>(null);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [showDimensionWarning, setShowDimensionWarning] = useState(false);

    useEffect(() => {
        if (imageFile && open) {
            const url = URL.createObjectURL(imageFile);
            setImageUrl(url);
            setShowDimensionWarning(false);

            return () => {
                URL.revokeObjectURL(url);
            };
        }
    }, [imageFile, open]);

    useEffect(() => {
        if (imageRef.current && imageUrl && open) {
            cropperRef.current = new Cropper(imageRef.current, {
                initialAspectRatio: aspectRatio,
                viewMode: 1,
                autoCropArea: 1,
                responsive: true,
                background: false,
                zoomable: true,
                scalable: true,
                movable: true,
                cropBoxResizable: true,
            });

            return () => {
                cropperRef.current?.destroy();
                cropperRef.current = null;
            };
        }
    }, [imageUrl, aspectRatio, open]);

    const handleConfirm = async () => {
        if (!cropperRef.current || !imageFile) return;

        setIsProcessing(true);

        try {
            const canvas = cropperRef.current.getCroppedCanvas();
            const croppedWidth = canvas.width;
            const croppedHeight = canvas.height;

            // Calculate optimal output dimensions
            let outputWidth = croppedWidth;
            let outputHeight = croppedHeight;

            if (minWidth && minHeight) {
                // Maximum allowed dimensions are 3x the minimum dimensions
                const maxAllowedWidth = minWidth * 3;
                const maxAllowedHeight = minHeight * 3;

                // Check if we need to scale down
                if (
                    croppedWidth > maxAllowedWidth ||
                    croppedHeight > maxAllowedHeight
                ) {
                    // Calculate the scale factor based on which dimension exceeds more
                    const widthScale = maxAllowedWidth / croppedWidth;
                    const heightScale = maxAllowedHeight / croppedHeight;

                    // Use the smaller scale to ensure both dimensions fit within limits
                    const scale = Math.min(widthScale, heightScale);

                    outputWidth = Math.round(croppedWidth * scale);
                    outputHeight = Math.round(croppedHeight * scale);
                }
            }

            // Check if final output dimensions meet minimum requirements
            const needsWarning =
                minWidth &&
                minHeight &&
                (outputWidth < minWidth || outputHeight < minHeight);

            // Get the scaled canvas if dimensions changed, otherwise use original
            const outputCanvas =
                outputWidth !== croppedWidth || outputHeight !== croppedHeight
                    ? cropperRef.current.getCroppedCanvas({
                          width: outputWidth,
                          height: outputHeight,
                      })
                    : canvas;

            // Compress the image to 90% quality
            outputCanvas.toBlob(
                (blob) => {
                    if (blob) {
                        if (needsWarning && !showDimensionWarning) {
                            // First time showing warning - don't proceed yet
                            setShowDimensionWarning(true);
                            setIsProcessing(false);
                        } else {
                            // Either dimensions are OK, or user confirmed despite warning
                            const croppedFile = new File(
                                [blob],
                                imageFile.name,
                                {
                                    type: imageFile.type,
                                    lastModified: Date.now(),
                                }
                            );
                            onConfirm(croppedFile);
                            setShowDimensionWarning(false);
                            setIsProcessing(false);
                        }
                    } else {
                        setIsProcessing(false);
                    }
                },
                imageFile.type,
                0.9
            );
        } catch {
            displaySnackbar(translate('cropImageFailedMessage'), {
                variant: 'error',
            });

            setIsProcessing(false);
        }
    };

    const handleCancel = () => {
        cropperRef.current?.destroy();
        cropperRef.current = null;
        setImageUrl('');
        setShowDimensionWarning(false);
        onCancel();
    };

    return (
        <AlertDialog
            actions={
                <>
                    <Button
                        autoFocus
                        color="primary"
                        disabled={isProcessing}
                        loading={isProcessing}
                        variant="contained"
                        onClick={handleConfirm}
                    >
                        {translate('ok')}
                    </Button>
                    <Button
                        disabled={isProcessing}
                        variant="contained"
                        onClick={handleCancel}
                    >
                        {translate('cancel')}
                    </Button>
                </>
            }
            maxWidth="md"
            open={open}
            title={translate('cropImage')}
            onClose={handleCancel}
        >
            <DialogContentText color="text.primary" variant="body2">
                {translate('cropImageDescription')}
            </DialogContentText>

            <Box
                sx={{
                    mt: 2,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    '& img': {
                        maxWidth: '100%',
                        maxHeight: '60vh',
                        height: 'auto',
                        display: 'block',
                    },
                }}
            >
                {imageUrl && (
                    <img alt="Crop preview" ref={imageRef} src={imageUrl} />
                )}

                {showDimensionWarning && (
                    <DialogContentText
                        color="error"
                        sx={{ mt: 2 }}
                        variant="body2"
                    >
                        {translate('cropImageDimensionWarning', {
                            minWidth,
                            minHeight,
                        })}
                    </DialogContentText>
                )}
            </Box>
        </AlertDialog>
    );
};
